import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useSession } from "@vega-api/custom/SessionContext";
import {
  getGetTasksQueryKey,
  useGetTasks,
} from "@vega-api/generated/lp-tasks/lp-tasks";
import type { GetLpTasksResponse } from "@vega-api/generated/types/getLpTasksResponse";
import type { LpTaskResponse } from "@vega-api/generated/types/lpTaskResponse";
import { LocalStorageKeys } from "@vega-core/constants/cookies";
import { useLocaleStorage } from "@vega-core/hooks/useLocaleStorage";
import { isTruthy } from "@vega-core/utils/isTruthy";

const TASKS_LOCAL_STORAGE_KEY = LocalStorageKeys.TasksState;

const EMPTY_TASKS_RESPONSE: GetLpTasksResponse = {
  data: [],
  meta: null,
  errors: {},
};

type TaskState = {
  completed: boolean;
};

type UseTaskStateV2Args = {
  enabled: boolean;
};

export function useTaskStateV2({ enabled }: UseTaskStateV2Args) {
  const [tasksState, setTasksState] = useLocaleStorage<TaskState>(
    TASKS_LOCAL_STORAGE_KEY,
    { completed: false },
  );

  const { user } = useSession();
  const queryClient = useQueryClient();

  const { data: response, isError } = useGetTasks({
    query: {
      enabled: false,
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  });

  const hasData = response !== undefined;

  const tasksFromResponse = Array.isArray(response)
    ? response
    : (response?.data ?? []);

  const pendingTasks: LpTaskResponse[] = tasksFromResponse
    .slice()
    .sort((a, b) => a.displayOrder - b.displayOrder);

  const hasPendingTasks = hasData && pendingTasks.length > 0;

  const isCompleted = hasData && !hasPendingTasks;

  useEffect(
    function syncCompletedWithCache() {
      if (!hasData) return;

      if (tasksState.completed !== isCompleted) {
        setTasksState({ completed: isCompleted });
      }
    },
    [hasData, isCompleted, tasksState.completed, setTasksState],
  );

  const markCompleted = () => {
    queryClient.setQueryData(getGetTasksQueryKey(), EMPTY_TASKS_RESPONSE);
    setTasksState({ completed: true });
  };

  const isLoading = enabled && isTruthy(user) && !hasData;

  return {
    pendingTasks,
    isLoading,
    isError,
    isFetched: hasData,
    hasPendingTasks,
    isCompleted,
    refetch: markCompleted,
  };
}
